// frontend/app/TopicSelectionModal.js
import React, { useMemo, useState, useEffect } from "react";
import { Modal, View, Text, TouchableOpacity, ScrollView, Switch, StyleSheet } from "react-native";
import { normalizeQuizQuestions } from "./quizNormalize";

/**
 * Props:
 * - visible
 * - onClose()
 * - onApply({ videos_keep_tag_ids, pwos_keep_scene_names, quiz_keep_question_ids })
 * - template: { videos_payload, pwos_payload, quiz_payload, topic_snapshot }
 * - isDarkMode
 */
export default function TopicSelectionModal({ visible, onClose, onApply, template, isDarkMode = false }) {
  // BDD 04/28/2026
  // const videos = template?.videos_payload || [];
  // const pwos   = template?.pwos_payload   || [];
  // const quizRaw = template?.quiz_payload  || [];
  // const quiz = useMemo(() => normalizeQuizQuestions(quizRaw), [quizRaw]);

  // // Convert to toggle maps
  // const initialVideoMap = useMemo(() => Object.fromEntries(videos.map(v => [String(v.tag_id), true])), [videos]);
  // const pwoKey = (p) => `${p.pwo_source_id ?? p.pwo_id}|${p.scene_name}`;
  // const initialPwoMap   = useMemo(() => Object.fromEntries(pwos.map(p => [pwoKey(p), true])), [pwos]);
  // const initialQuizMap  = useMemo(() => Object.fromEntries(quiz.map(q => [String(q.question_id), true])), [quiz]);

  // BDD 04/28/2026
  const videos = template?.videos_payload || [];
  const pwos   = template?.pwos_payload   || [];
  const quizRaw = template?.quiz_payload  || [];
  const quiz = useMemo(() => normalizeQuizQuestions(quizRaw), [quizRaw]);

  // BDD 04/28/2026
  // Support both legacy tagged video segments and newer full-video rows with no tag_id.
  const videoKey = (v) => {
    const tagId = v?.tag_id;
    if (tagId !== null && tagId !== undefined && String(tagId) !== "") {
      return `tag:${String(tagId)}`;
    }
    const vid = v?.video_id ?? v?.id ?? v?.source_id;
    return `video:${String(vid)}`;
  };

  // Convert to toggle maps
  const initialVideoMap = useMemo(
    () => Object.fromEntries(videos.map(v => [videoKey(v), true])),
    [videos]
  );
  const pwoKey = (p) => `${p.pwo_source_id ?? p.pwo_id}|${p.scene_name}`;
  const initialPwoMap   = useMemo(() => Object.fromEntries(pwos.map(p => [pwoKey(p), true])), [pwos]);
  const initialQuizMap  = useMemo(() => Object.fromEntries(quiz.map(q => [String(q.question_id), true])), [quiz]);

  const [videoMap, setVideoMap] = useState(initialVideoMap);
  const [pwoMap,   setPwoMap]   = useState(initialPwoMap);
  const [quizMap,  setQuizMap]  = useState(initialQuizMap);

  useEffect(() => { setVideoMap(initialVideoMap); }, [visible, initialVideoMap]);
  useEffect(() => { setPwoMap(initialPwoMap); }, [visible, initialPwoMap]);
  useEffect(() => { setQuizMap(initialQuizMap); }, [visible, initialQuizMap]);
//// BDD 04/28/2026
//   useEffect(() => {
//   console.log("[COLL DEBUG][TopicSelectionModal] template changed", {
//     visible,
//     videosCount: videos.length,
//     pwosCount: pwos.length,
//     quizCount: quiz.length,
//     first5Videos: videos.slice(0, 5).map(v => ({
//       id: v?.id,
//       video_id: v?.video_id,
//       source_id: v?.source_id,
//       tag_id: v?.tag_id,
//       name: v?.name,
//       tag_start_time: v?.tag_start_time,
//       is_full_video: v?.is_full_video,
//     })),
//   });
// }, [visible, videos, pwos, quiz]);

  // BDD 04/28/2026
  // const apply = () => {
  //   const videos_keep_tag_ids = Object.entries(videoMap).filter(([_, on]) => on).map(([k]) => Number(k));
  //   const pwos_keep_scene_names = Object.entries(pwoMap).filter(([_, on]) => on).map(([k]) => k.includes("|") ? k.split("|")[1] : k);
  //   const quiz_keep_question_ids = Object.entries(quizMap).filter(([_, on]) => on).map(([k]) => Number(k));
  //   onApply({ videos_keep_tag_ids, pwos_keep_scene_names, quiz_keep_question_ids });
  // };

  // BDD 04/28/2026
  const apply = () => {
    const selectedVideoKeys = Object.entries(videoMap)
      .filter(([_, on]) => on)
      .map(([k]) => k);

    const videos_keep_tag_ids = selectedVideoKeys
      .filter((k) => k.startsWith("tag:"))
      .map((k) => Number(k.slice(4)))
      .filter((n) => Number.isFinite(n));

    const videos_keep_video_ids_no_tag = selectedVideoKeys
      .filter((k) => k.startsWith("video:"))
      .map((k) => Number(k.slice(6)))
      .filter((n) => Number.isFinite(n));

    const pwos_keep_scene_names = Object.entries(pwoMap)
      .filter(([_, on]) => on)
      .map(([k]) => (k.includes("|") ? k.split("|")[1] : k));

    const quiz_keep_question_ids = Object.entries(quizMap)
      .filter(([_, on]) => on)
      .map(([k]) => Number(k));

    onApply({
      videos_keep_tag_ids,
      videos_keep_video_ids_no_tag,
      pwos_keep_scene_names,
      quiz_keep_question_ids,
    });
  };
  // helper to get first-line text for quiz label
  const quizTitle = (q) => {
    const txt = q?.question?.text || (typeof q?.question === "string" ? q.question : "");
    return (txt || "").slice(0, 80) + ((txt || "").length > 80 ? "…" : "");
  };

  const dk = isDarkMode;
  const s = dk ? darkStyles : lightStyles;

  return (
    <Modal visible={visible} animationType="slide" transparent={false} onRequestClose={onClose}>
      <View style={s.header}>
        <Text style={s.title}>Customize Topic</Text>
        <TouchableOpacity onPress={onClose}><Text style={s.link}>Close</Text></TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={s.body}>
        <Text style={s.section}>🎞️ Video Segments</Text>
        {videos.length === 0 ? ( 
          <>
            <Text style={s.dim}>No segments</Text>
          </>
        ) : (
          videos.map((v) => {
            const k = videoKey(v);
            const isFullVideo = !!v?.is_full_video || v?.tag_id == null;
            const timeLabel = isFullVideo ? "t=0:00s" : `t=${v.tag_start_time ?? 0}s`;

            return (
              <RowSwitch
                key={k}
                label={`${v.name}  ·  ${timeLabel}`}
                value={!!videoMap[k]}
                onValueChange={(val) => setVideoMap((m) => ({ ...m, [k]: val }))}
                isDarkMode={dk}
              />
            );
          })
        )}
        <Text style={s.section}>🧪 PWO Scenes</Text>
        {pwos.length === 0 ? <Text style={s.dim}>No scenes</Text> : pwos.map(p => (
          <RowSwitch
            key={pwoKey(p)}
            label={`${p.name}${p.image_path ? "  🖼️" : ""}`}
            value={!!pwoMap[pwoKey(p)]}
            onValueChange={val => setPwoMap(m => ({ ...m, [pwoKey(p)]: val }))}
            isDarkMode={dk}
          />
        ))}

        <Text style={s.section}>📝 Quiz Questions</Text>
        {quiz.length === 0 ? <Text style={s.dim}>No questions</Text> : quiz.map(q => (
          <RowSwitch
            key={q.question_id}
            label={quizTitle(q)}
            value={!!quizMap[String(q.question_id)]}
            onValueChange={val => setQuizMap(m => ({ ...m, [String(q.question_id)]: val }))}
            isDarkMode={dk}
          />
        ))}

        <TouchableOpacity style={s.primaryBtn} onPress={apply}>
          <Text style={s.primaryText}>Apply</Text>
        </TouchableOpacity>
      </ScrollView>
    </Modal>
  );
}


function RowSwitch({ label, value, onValueChange, isDarkMode }) {
  return (
    <View style={isDarkMode ? darkStyles.row : lightStyles.row}>
      <Text style={isDarkMode ? darkStyles.rowText : lightStyles.rowText}>{label}</Text>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: isDarkMode ? "#334155" : "#e2e8f0", true: isDarkMode ? "rgba(99, 102, 241, 0.4)" : "#6ee7b7" }}
        thumbColor={value ? (isDarkMode ? "#818cf8" : "#10b981") : (isDarkMode ? "#94a3b8" : "#ffffff")}
      />
    </View>
  );
}

const lightStyles = StyleSheet.create({
  header: {
    paddingTop: 50,
    paddingHorizontal: 16,
    paddingBottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#0077b6",
  },
  title: { color: "#fff", fontSize: 18, fontWeight: "600" },
  link: { color: "#fff", fontSize: 16, fontWeight: "700" },

  body: { padding: 16, gap: 12, backgroundColor: "#fff" },
  section: { marginTop: 8, fontWeight: "700", fontSize: 16, color: "#333" },
  dim: { color: "#666", marginVertical: 6 },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#eef2f7",
  },
  rowText: { flex: 1, paddingRight: 8, color: "#222" },

  primaryBtn: { marginTop: 18, backgroundColor: "#0077b6", paddingVertical: 12, borderRadius: 10, alignItems: "center" },
  primaryText: { color: "white", fontWeight: "700" },
});

const darkStyles = StyleSheet.create({
  header: {
    paddingTop: 50,
    paddingHorizontal: 16,
    paddingBottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#1e293b",
  },
  title: { color: "#f1f5f9", fontSize: 18, fontWeight: "600" },
  link: { color: "#818cf8", fontSize: 16, fontWeight: "700" },

  body: { padding: 16, gap: 12, backgroundColor: "#0f172a" },
  section: { marginTop: 8, fontWeight: "700", fontSize: 16, color: "#f1f5f9" },
  dim: { color: "#64748b", marginVertical: 6 },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(148, 163, 184, 0.15)",
  },
  rowText: { flex: 1, paddingRight: 8, color: "#e2e8f0" },

  primaryBtn: { marginTop: 18, backgroundColor: "#6366f1", paddingVertical: 12, borderRadius: 10, alignItems: "center" },
  primaryText: { color: "white", fontWeight: "700" },
});
